import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Customer {
  id: number;
  name: string;
  cpf: string;
  total_points: number;
  store: {
    id: number;
    name: string;
    description?: string;
  };
}

interface CustomerAuthContextType {
  customer: Customer | null;
  token: string | null;
  login: (token: string, customer: Customer) => void;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar se há token salvo no localStorage
    const savedToken = localStorage.getItem('customer_token');
    const savedCustomer = localStorage.getItem('customer_data');

    if (savedToken && savedCustomer) {
      try {
        const customerData = JSON.parse(savedCustomer);
        setToken(savedToken);
        setCustomer(customerData);
      } catch (error) {
        console.error('Erro ao recuperar dados do cliente:', error);
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_data');
      }
    }

    setLoading(false);
  }, []);

  const login = (newToken: string, customerData: Customer) => {
    setToken(newToken);
    setCustomer(customerData);
    localStorage.setItem('customer_token', newToken);
    localStorage.setItem('customer_data', JSON.stringify(customerData));
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
  };

  const value: CustomerAuthContextType = {
    customer,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!customer,
    loading,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth deve ser usado dentro de um CustomerAuthProvider');
  }
  return context;
};
